import * as React from 'react';
import { cn } from '../../lib/utils';

export interface CodeInputProps {
  length?: number;
  value: string;
  onChange: (_value: string) => void;
  /** Fired once the last box is filled, so the form can submit itself. */
  onComplete?: (_value: string) => void;
  /** Name of the hidden field carrying the whole code, for form submission. */
  name?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
}

/**
 * A verification code entered a digit at a time.
 *
 * Separate boxes make the expected length obvious and stop the transposition
 * mistakes a single long field invites. The things that usually break in this
 * pattern are handled explicitly: pasting the whole code works from any box,
 * backspace on an empty box steps back, and arrow keys move without editing.
 *
 * The full value is mirrored into a hidden input so form submission and
 * autofill see one field, and so the existing `name="code"` contract that e2e
 * specs rely on still holds.
 */
export function CodeInput({
  length = 6,
  value,
  onChange,
  onComplete,
  name,
  label,
  error,
  disabled,
  autoFocus,
  className,
}: CodeInputProps) {
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);
  const digits = React.useMemo(
    () => Array.from({ length }, (_, index) => value[index] ?? ''),
    [value, length],
  );

  const commit = (next: string) => {
    const trimmed = next.slice(0, length);
    onChange(trimmed);
    if (trimmed.length === length) onComplete?.(trimmed);
  };

  const setDigit = (index: number, digit: string) => {
    const next = digits.slice();
    next[index] = digit;
    commit(next.join('').replace(/\s/g, ''));
  };

  const handleChange = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) {
      setDigit(index, '');
      return;
    }

    // More than one character means a paste (or a fast typist landing two
    // keystrokes in one event) — spread it across the boxes from here on.
    if (typed.length > 1) {
      const merged = digits.slice(0, index).join('') + typed;
      commit(merged.slice(0, length));
      refs.current[Math.min(index + typed.length, length - 1)]?.focus();
      return;
    }

    setDigit(index, typed);
    refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      // Nothing here to delete, so step back and clear that one instead —
      // otherwise backspace appears to do nothing.
      event.preventDefault();
      setDigit(index - 1, '');
      refs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  return (
    <div className={className}>
      {label && <span className="mb-1.5 block text-sm font-medium text-foreground">{label}</span>}
      <div className="flex justify-center gap-2" role="group" aria-label={label ?? 'Verification code'}>
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={length}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
            aria-label={`Digit ${index + 1} of ${length}`}
            aria-invalid={error ? true : undefined}
            data-testid={`code-digit-${index}`}
            value={digit}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onFocus={(event) => event.target.select()}
            className={cn(
              'h-12 w-11 rounded-lg border bg-card text-center text-lg font-medium tabular-nums text-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
              error ? 'border-destructive' : 'border-input',
              disabled && 'opacity-60',
            )}
          />
        ))}
      </div>
      {name && <input type="hidden" name={name} value={value} />}
      {error && <p className="mt-2 text-center text-sm text-destructive">{error}</p>}
    </div>
  );
}
